import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Download, TrendingUp, Wallet, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';

const fmtINR = (paise) => `₹${((paise || 0) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/**
 * Reusable finance summary panel.
 * - Industry sees: gross paid per project + monthly totals ("You paid").
 * - Innovator sees: 80% share per project + monthly earnings ("You earned").
 * - Admin sees: gross, expert share, platform fee.
 * Supports year / month filtering and CSV download.
 */
export default function FinanceSummary({ API, token, defaultRole }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [year, setYear] = useState('all');
  const [month, setMonth] = useState('all');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/projects/finance/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setData(await res.json());
        else toast.error('Failed to load finance data');
      } finally { setLoading(false); }
    })();
  }, [API, token]);

  const role = data?.role || defaultRole || 'industry';

  const years = useMemo(() => {
    if (!data) return [];
    const s = new Set();
    (data.rows || []).forEach(r => { if (r.month) s.add(r.month.slice(0, 4)); });
    return Array.from(s).sort().reverse();
  }, [data]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    return (data.rows || []).filter(r => {
      if (year !== 'all' && !(r.month || '').startsWith(year)) return false;
      if (month !== 'all' && !(r.month || '').endsWith(`-${month}`)) return false;
      return true;
    });
  }, [data, year, month]);

  const filteredTotals = useMemo(() => {
    const t = { gross_paise: 0, expert_share_paise: 0, platform_paise: 0, meetings_paise: 0 };
    filteredRows.forEach(r => {
      t.gross_paise += r.gross_paise || 0;
      t.expert_share_paise += r.expert_share_paise || 0;
      t.platform_paise += r.platform_paise || 0;
      if (r.kind === 'meeting') t.meetings_paise += r.gross_paise || 0;
    });
    return t;
  }, [filteredRows]);

  const monthlyFiltered = useMemo(() => {
    const agg = {};
    filteredRows.forEach(r => {
      const k = r.month || 'unknown';
      const m = agg[k] || (agg[k] = { month: k, gross_paise: 0, expert_share_paise: 0, platform_paise: 0, count: 0 });
      m.gross_paise += r.gross_paise || 0;
      m.expert_share_paise += r.expert_share_paise || 0;
      m.platform_paise += r.platform_paise || 0;
      m.count += 1;
    });
    return Object.values(agg).sort((a, b) => (b.month || '').localeCompare(a.month || ''));
  }, [filteredRows]);

  const downloadCsv = () => {
    if (!filteredRows.length) { toast.error('Nothing to export'); return; }
    const headers = ['Date', 'Type', 'Title', 'Status', 'Gross (INR)', role === 'admin' ? 'Expert Share (INR)' : (role === 'expert' ? 'Your Earnings (INR)' : 'You Paid (INR)'), 'Platform Fee (INR)'];
    const lines = [headers.join(',')];
    filteredRows.forEach(r => {
      const primary = role === 'industry' ? r.gross_paise : r.expert_share_paise;
      lines.push([
        (r.date || '').slice(0, 19),
        r.kind,
        JSON.stringify(r.title || ''),
        r.status || '',
        (r.gross_paise / 100).toFixed(2),
        (primary / 100).toFixed(2),
        (r.platform_paise / 100).toFixed(2),
      ].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `collabhive-finance-${role}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }
  if (!data) return <p className="text-sm text-muted-foreground">No finance data available.</p>;

  const primaryLabel = role === 'industry' ? 'Total Spent' : (role === 'expert' ? 'Total Earnings (80%)' : 'Gross Volume');
  const primaryValue = role === 'industry' ? filteredTotals.gross_paise : filteredTotals.expert_share_paise;

  return (
    <div className="space-y-4" data-testid="finance-summary">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{primaryLabel}</p>
          <p className="text-lg sm:text-2xl font-bold mt-1 flex items-center gap-1"><IndianRupee className="w-4 h-4" />{(primaryValue / 100).toLocaleString('en-IN')}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Transactions</p>
          <p className="text-lg sm:text-2xl font-bold mt-1">{filteredRows.length}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{role === 'admin' ? 'Platform Fee' : 'Meetings'}</p>
          <p className="text-lg sm:text-2xl font-bold mt-1">{fmtINR(role === 'admin' ? filteredTotals.platform_paise : filteredTotals.meetings_paise)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{role === 'admin' ? 'Gross Volume' : 'Gross'}</p>
          <p className="text-lg sm:text-2xl font-bold mt-1">{fmtINR(filteredTotals.gross_paise)}</p>
        </CardContent></Card>
      </div>

      {/* Filters + download */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-28" data-testid="finance-year-filter"><SelectValue placeholder="Year" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All years</SelectItem>
            {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-32" data-testid="finance-month-filter"><SelectValue placeholder="Month" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All months</SelectItem>
            {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m =>
              <SelectItem key={m} value={m}>{new Date(2000, Number(m)-1, 1).toLocaleString('en', { month: 'long' })}</SelectItem>
            )}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={downloadCsv} className="gap-2" data-testid="download-finance-csv">
          <Download className="w-4 h-4" /> Download CSV
        </Button>
      </div>

      {/* Monthly table */}
      <Card>
        <CardContent className="p-4">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /> Monthly Summary</h4>
          {monthlyFiltered.length === 0 ? (
            <p className="text-xs text-muted-foreground">No transactions for the selected filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2 pr-2">Month</th>
                    <th className="text-right py-2 px-2">#</th>
                    <th className="text-right py-2 px-2">Gross</th>
                    <th className="text-right py-2 pl-2">{role === 'industry' ? 'You Paid' : 'Earnings'}</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyFiltered.map(m => (
                    <tr key={m.month} className="border-b last:border-0">
                      <td className="py-2 pr-2">{m.month || '—'}</td>
                      <td className="py-2 px-2 text-right">{m.count}</td>
                      <td className="py-2 px-2 text-right">{fmtINR(m.gross_paise)}</td>
                      <td className="py-2 pl-2 text-right font-medium">{fmtINR(role === 'industry' ? m.gross_paise : m.expert_share_paise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transactions */}
      <Card>
        <CardContent className="p-4">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Wallet className="w-4 h-4 text-primary" /> Transactions</h4>
          {filteredRows.length === 0 ? (
            <p className="text-xs text-muted-foreground">No transactions.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2 pr-2">Date</th>
                    <th className="text-left py-2 px-2">Item</th>
                    <th className="text-left py-2 px-2">Type</th>
                    <th className="text-right py-2 pl-2">{role === 'industry' ? 'You Paid' : 'Earnings'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 pr-2 whitespace-nowrap">{(r.date || '').slice(0, 10)}</td>
                      <td className="py-2 px-2 max-w-[240px] truncate" title={r.title}>{r.title}</td>
                      <td className="py-2 px-2"><Badge variant="secondary" className="text-[10px]">{r.kind}</Badge></td>
                      <td className="py-2 pl-2 text-right font-medium">{fmtINR(role === 'industry' ? r.gross_paise : r.expert_share_paise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
